import { useMemo, useState } from 'react';
import useSWR from 'swr';

import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@workspace/ui';

import { MappingAssetAccessSheet } from '@/components/mapping/MappingAssetAccessSheet';
import { MappingFeatureUnavailable, MappingPermissionDenied } from '@/components/mapping/MappingFeatureUnavailable';
import {
  buildGlyphRuntimeUrl,
  formatShareScopeLabel,
  isPortalAssetReadOnly,
  normalizeShareScope
} from '@/lib/mapping-assets';
import {
  deleteGlyphSet,
  listGlyphSetShares,
  listGlyphSets,
  replaceGlyphSetShares,
  updateGlyphSet,
  uploadGlyphSet,
  type GlyphSet,
  type GlyphSetUploadPayload,
  type ShareScope
} from '@/lib/mapping-api';
import { useMappingAccess } from '@/lib/useMappingAccess';

const DEFAULT_PAGE_SIZE = 200;

const formatDate = (value?: string | null) => {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString();
  } catch {
    return value;
  }
};

export default function MapGlyphsPage() {
  const mappingAccess = useMappingAccess();
  const [shareScopeFilter, setShareScopeFilter] = useState<'all' | ShareScope>('all');
  const [search, setSearch] = useState('');
  const [sheetOpen, setSheetOpen] = useState(false);
  const [replaceTarget, setReplaceTarget] = useState<GlyphSet | null>(null);
  const [accessTarget, setAccessTarget] = useState<GlyphSet | null>(null);
  const [accessOpen, setAccessOpen] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [uploadState, setUploadState] = useState<{
    name: string;
    description: string;
    shareScope: ShareScope;
    bundle: File | null;
  }>({
    name: '',
    description: '',
    shareScope: 'tenant',
    bundle: null
  });

  const { data, error, isLoading, mutate } = useSWR<GlyphSet[]>(
    mappingAccess.accessEnabled ? ['glyph-sets', shareScopeFilter] : null,
    async () =>
      listGlyphSets({
        limit: DEFAULT_PAGE_SIZE,
        share_scope: shareScopeFilter === 'all' ? undefined : shareScopeFilter
      })
  );

  const filteredGlyphs = useMemo(() => {
    if (!data) return [];
    const needle = search.trim().toLowerCase();
    if (!needle) return data;
    return data.filter(glyph =>
      [glyph.name, glyph.description, glyph.id, ...(glyph.fontstacks ?? [])]
        .filter(Boolean)
        .some(value => value!.toLowerCase().includes(needle))
    );
  }, [data, search]);

  const openUpload = (target?: GlyphSet) => {
    setReplaceTarget(target ?? null);
    setFormError(null);
    setUploadState({
      name: target?.name ?? '',
      description: target?.description ?? '',
      shareScope: normalizeShareScope(target?.share_scope),
      bundle: null
    });
    setSheetOpen(true);
  };

  const handleUpload = async () => {
    setFormError(null);
    if (!uploadState.bundle) {
      setFormError('Select a glyph bundle before uploading.');
      return;
    }

    const name = uploadState.name.trim() || replaceTarget?.name || '';
    if (!name) {
      setFormError('Name is required.');
      return;
    }

    setSaving(true);
    const payload: GlyphSetUploadPayload = {
      name,
      description: uploadState.description.trim() || undefined,
      share_scope: uploadState.shareScope,
      bundle: uploadState.bundle,
      glyph_set_id: replaceTarget?.id
    };

    try {
      await uploadGlyphSet(payload);
      await mutate();
      setSheetOpen(false);
      setReplaceTarget(null);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Upload failed';
      setFormError(message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (glyph: GlyphSet) => {
    if (!glyph.id) {
      return;
    }
    if (!window.confirm(`Delete glyph set "${glyph.name ?? glyph.id}"?`)) {
      return;
    }
    try {
      await deleteGlyphSet(glyph.id);
      await mutate();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Delete failed';
      setFormError(message);
    }
  };

  const handleCopyRuntime = async (glyph: GlyphSet) => {
    const runtimeUrl = buildGlyphRuntimeUrl(glyph.id);
    if (!runtimeUrl) {
      return;
    }
    try {
      await navigator.clipboard.writeText(runtimeUrl);
    } catch {
      setFormError('Failed to copy the glyph runtime URL.');
    }
  };

  if (!mappingAccess.mappingFeatureEnabled) {
    return <MappingFeatureUnavailable />;
  }

  if (!mappingAccess.mappingPermissionGranted) {
    return <MappingPermissionDenied />;
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">Map Glyphs</h1>
          <p className="text-sm text-muted-foreground">Upload glyph bundles that styles can reference for labels and symbols.</p>
        </div>
        <Button onClick={() => openUpload()}>Upload glyph set</Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Glyph Sets</CardTitle>
          <CardDescription>Glyph assets visible to the current tenant context.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <Input value={search} onChange={event => setSearch(event.target.value)} placeholder="Search glyph sets" />
            <Select value={shareScopeFilter} onValueChange={value => setShareScopeFilter(value as 'all' | ShareScope)}>
              <SelectTrigger className="sm:w-[180px]">
                <SelectValue placeholder="Scope" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All scopes</SelectItem>
                <SelectItem value="public">Public</SelectItem>
                <SelectItem value="shared">Shared</SelectItem>
                <SelectItem value="tenant">Tenant</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {formError ? (
            <div className="rounded border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">
              {formError}
            </div>
          ) : null}

          {error ? (
            <div className="rounded border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">
              {error.message}
            </div>
          ) : null}

          {isLoading ? (
            <div className="space-y-2">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </div>
          ) : filteredGlyphs.length === 0 ? (
            <div className="text-sm text-muted-foreground">No glyph sets found.</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Scope</TableHead>
                  <TableHead>Fontstacks</TableHead>
                  <TableHead>Updated</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredGlyphs.map(glyph => {
                  const readOnly = isPortalAssetReadOnly(glyph.tenant_id, mappingAccess.featureTenantId);
                  return (
                    <TableRow key={glyph.id}>
                      <TableCell className="font-medium text-foreground">
                        <div className="space-y-1">
                          <div className="flex items-center gap-2">
                            <span>{glyph.name}</span>
                            {readOnly ? <Badge variant="outline">Read-only</Badge> : null}
                          </div>
                          <div className="text-xs text-muted-foreground">{glyph.id}</div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="secondary" className="capitalize">
                          {formatShareScopeLabel(glyph.share_scope)}
                        </Badge>
                      </TableCell>
                      <TableCell className="max-w-[320px] text-xs text-muted-foreground">
                        {glyph.fontstacks?.length ? glyph.fontstacks.join(', ') : '—'}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">{formatDate(glyph.updated_at)}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button variant="outline" size="sm" onClick={() => handleCopyRuntime(glyph)}>
                            Copy runtime
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setAccessTarget(glyph);
                              setAccessOpen(true);
                            }}
                          >
                            Access
                          </Button>
                          {!readOnly ? (
                            <Button variant="outline" size="sm" onClick={() => openUpload(glyph)}>
                              Replace
                            </Button>
                          ) : null}
                          {!readOnly ? (
                            <Button variant="ghost" size="sm" className="text-destructive" onClick={() => handleDelete(glyph)}>
                              Delete
                            </Button>
                          ) : null}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent side="right" size="lg">
          <SheetHeader>
            <SheetTitle>{replaceTarget ? 'Replace glyph set' : 'Upload glyph set'}</SheetTitle>
            <SheetDescription>Upload a compressed glyph bundle for the fontstacks you want to serve.</SheetDescription>
          </SheetHeader>

          <div className="mt-6 space-y-4">
            <div className="space-y-2">
              <Label htmlFor="glyph-name">Name</Label>
              <Input
                id="glyph-name"
                value={uploadState.name}
                onChange={event => setUploadState(prev => ({ ...prev, name: event.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="glyph-description">Description</Label>
              <Input
                id="glyph-description"
                value={uploadState.description}
                onChange={event => setUploadState(prev => ({ ...prev, description: event.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="glyph-scope">Share scope</Label>
              <Select
                value={uploadState.shareScope}
                onValueChange={value => setUploadState(prev => ({ ...prev, shareScope: value as ShareScope }))}
              >
                <SelectTrigger id="glyph-scope">
                  <SelectValue placeholder="Scope" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="public">Public</SelectItem>
                  <SelectItem value="shared">Shared</SelectItem>
                  <SelectItem value="tenant">Tenant</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="glyph-bundle">Glyph bundle</Label>
              <Input
                id="glyph-bundle"
                type="file"
                accept=".zip,.tar,.tar.gz,.tgz,application/zip,application/gzip"
                onChange={event => setUploadState(prev => ({ ...prev, bundle: event.target.files?.[0] ?? null }))}
              />
            </div>
            {formError ? (
              <div className="rounded border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">
                {formError}
              </div>
            ) : null}
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setSheetOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleUpload} disabled={saving}>
                {saving ? 'Saving…' : replaceTarget ? 'Replace glyph set' : 'Upload glyph set'}
              </Button>
            </div>
          </div>
        </SheetContent>
      </Sheet>

      <MappingAssetAccessSheet
        open={accessOpen}
        onOpenChange={setAccessOpen}
        assetTypeLabel="Glyph set"
        asset={accessTarget}
        featureTenantId={mappingAccess.featureTenantId}
        loadShares={assetId => listGlyphSetShares(assetId)}
        saveAccess={async (asset, input) => {
          if (!asset.id) {
            throw new Error('Glyph set ID required');
          }
          await updateGlyphSet(asset.id, { share_scope: input.shareScope });
          await replaceGlyphSetShares(asset.id, input.tenantIds);
          await mutate();
        }}
      />
    </div>
  );
}
